import { useEffect } from "react";
import { Switch, Route, Link, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/components/theme-provider";
import { AuthProvider } from "@/contexts/auth-context";
import { useAuth } from "@/contexts/auth-context";
import Home from "@/pages/home";
import FormPage from "@/pages/form";
import PlanPage from "@/pages/plan";
import MyPlansPage from "@/pages/my-plans";
import MyCreditsPage from "@/pages/my-credits";
import LoginPage from "@/pages/login";
import SignupPage from "@/pages/signup";
import ForgotPasswordPage from "@/pages/forgot-password";
import ResetPasswordPage from "@/pages/reset-password";
import AuthCallbackPage from "@/pages/auth-callback";
import AuthTestPage from "@/pages/auth-test";
import NotFound from "@/pages/not-found";
import DatabaseSetupPage from "@/pages/database-setup";
import AuthSetupPage from "@/pages/auth-setup";
import DatabaseStatusPage from "@/pages/database-status";
import DatabaseInitPage from "@/pages/db-init";
import PricingPage from "@/pages/pricing";
import ContactPage from "@/pages/contact";
import MyConsultationsPage from "./pages/my-consultations";
import BlogPage from "@/pages/blog";
import BlogPostPage from "@/pages/blog-post";
import BlogTagPage from "@/pages/blog-tag";
// Coach system pages
import CoachDashboard from "@/pages/coach/dashboard";
import PlayerDetailPage from "@/pages/coach/player-detail";
import CreateTeamPage from "@/pages/create-team";
import JoinTeamPage from "@/pages/join-team";
import SettingsPage from "@/pages/settings";
import logger from '@/lib/logger';
import AnalyticsBindings from '@/components/analytics-bindings';

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { User as UserIcon, LogOut, Coins, MessageSquare, Users, Settings } from "lucide-react";
import FeedbackIcon from "@/components/feedback-icon";

function Router() {
  return (
    <Switch>
      <Route path="/" component={Home} />
      <Route path="/form" component={FormPage} />
      <Route path="/plan" component={PlanPage} />
      <Route path="/plan/:id" component={PlanPage} />
      <Route path="/my-plans" component={MyPlansPage} />
      <Route path="/my-credits" component={MyCreditsPage} />
      <Route path="/my-consultations" component={MyConsultationsPage} />
      <Route path="/pricing" component={PricingPage} />
      <Route path="/contact" component={ContactPage} />
      <Route path="/blog" component={BlogPage} />
      <Route path="/blog/:slug" component={BlogPostPage} />
      <Route path="/blog/tag/:tag" component={BlogTagPage} />
      
      <Route path="/login" component={LoginPage} />
      <Route path="/signup" component={SignupPage} />
      <Route path="/forgot-password" component={ForgotPasswordPage} />
      <Route path="/reset-password" component={ResetPasswordPage} />
      <Route path="/auth/callback" component={AuthCallbackPage} />

      {/* Dev-only routes - hidden in production builds */}
      {import.meta.env.DEV && (
        <>
          <Route path="/auth-test" component={AuthTestPage} />
          <Route path="/database-setup" component={DatabaseSetupPage} />
          <Route path="/auth-setup" component={AuthSetupPage} />
          <Route path="/database-status" component={DatabaseStatusPage} />
          <Route path="/db-init" component={DatabaseInitPage} />
        </>
      )}

      {/* Coach system routes */}
      <Route path="/coach/dashboard" component={CoachDashboard} />
      <Route path="/coach/players/:id" component={PlayerDetailPage} />
      <Route path="/create-team" component={CreateTeamPage} />
      <Route path="/join-team" component={JoinTeamPage} />
      <Route path="/settings" component={SettingsPage} />

      <Route component={NotFound} />
    </Switch>
  );
}

function AccountControls() {
  const { user, isAuthenticated, logout } = useAuth();
  const [, setLocation] = useLocation();

  const avatarUrl = user?.profilePicture || "";
  const displayName = user?.name || (user?.email as string) || "";
  const initial = displayName ? displayName.charAt(0).toUpperCase() : "U";

  const handleLogout = async () => {
    try {
      await logout();
      setLocation("/");
    } catch (e) {
      logger.error('Logout error', e as any);
    }
  };

  if (!isAuthenticated) {
    return <Link className="btn btn-primary" href="/login">Get Started</Link>;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label="Account menu"
          className="rounded-full w-9 h-9 overflow-hidden border border-white/20 hover:border-white/40 transition focus:outline-none focus:ring-2 focus:ring-white/40"
        >
          {avatarUrl ? (
            <img src={avatarUrl} alt="User avatar" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full grid place-items-center bg-white/10 text-white/90 text-sm">
              {initial}
            </div>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-48">
        <div className="px-3 py-2 text-sm opacity-80">
          {displayName}
        </div>
        <DropdownMenuSeparator />
        {/* Coach Dashboard - only for coaches */}
        {user?.accountType === 'coach' && (
          <DropdownMenuItem onSelect={() => setLocation('/coach/dashboard')}>
            <Users className="w-4 h-4 mr-2" /> Coach Dashboard
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onSelect={() => setLocation('/my-plans')}>
          <UserIcon className="w-4 h-4 mr-2" /> My Plans
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => setLocation('/my-credits')}>
          <Coins className="w-4 h-4 mr-2" /> My Credits
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => setLocation('/my-consultations')}>
          <MessageSquare className="w-4 h-4 mr-2" /> My Consultations
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => setLocation('/settings')}>
          <Settings className="w-4 h-4 mr-2" /> Settings
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={handleLogout}>
          <LogOut className="w-4 h-4 mr-2" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function App() {
  // Ensure dark mode is always applied
  document.documentElement.classList.add("dark");

  // One-time client log to validate frontend debug switch
  try {
    logger.info('App mounted', { mode: import.meta.env.MODE });
  } catch {}

  // Signal to prerenderer that the page is ready to be captured
  // Delay to allow react-helmet-async to update meta tags
  useEffect(() => {
    const timer = setTimeout(() => {
      document.dispatchEvent(new Event('prerender-ready'));
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <TooltipProvider>
            {/* Analytics bindings: pageviews + identify/reset */}
            <AnalyticsBindings />
            {/* Global app shell with landing page header/footer */}
            <div className="min-h-screen flex flex-col bg-background text-foreground">
              {/* Global Landing Header */}
              <header className="site-header" role="banner">
                <div className="container header-inner">
                  <Link className="brand" href="/" aria-label="BounceBunny Home">
                    <img className="logo" src="/assets/logo.png" alt="BounceBunny logo" />
                    <span className="brand-name">BounceBunny</span>
                  </Link>
                  <nav className="nav-actions" aria-label="Primary">
                    <Link className="link-pricing" href="/pricing">Pricing</Link>
                    <AccountControls />
                  </nav>
                </div>
              </header>

              <main className="flex-1">
                <Router />
              </main>

              {/* Global Landing Footer */}
              <footer className="site-footer" role="contentinfo">
                <div className="container footer-inner">
                  {/* Top row: Logo + Navigation columns */}
                  <div className="footer-top">
                    {/* Brand */}
                    <div className="footer-brand">
                      <Link href="/" className="footer-logo-link">
                        <img src="/assets/logo.png" alt="BounceBunny" className="footer-logo" />
                        <span className="footer-brand-name">BounceBunny</span>
                      </Link>
                    </div>

                    {/* Navigation columns */}
                    <div className="footer-nav-columns">
                      {/* Resources */}
                      <div className="footer-nav-column">
                        <h4 className="footer-nav-title">Resources</h4>
                        <nav aria-label="Resources">
                          <Link href="/blog">Blog</Link>
                          <Link href="/pricing">Pricing</Link>
                          <Link href="/contact">Contact</Link>
                        </nav>
                      </div>

                      {/* Legal */}
                      <div className="footer-nav-column">
                        <h4 className="footer-nav-title">Legal</h4>
                        <nav aria-label="Legal">
                          <a href="/privacy-policy">Privacy Policy</a>
                          <a href="/terms-of-service">Terms of Service</a>
                          <a href="/refund-policy">Refund Policy</a>
                        </nav>
                      </div>

                      {/* Follow us */}
                      <div className="footer-nav-column">
                        <h4 className="footer-nav-title">Follow Us</h4>
                        <nav aria-label="Social links">
                          <a 
                            href="https://www.instagram.com/bouncebunnyai/" 
                            target="_blank" 
                            rel="noopener noreferrer"
                          >
                            Instagram
                          </a>
                        </nav>
                      </div>
                    </div>
                  </div>

                  {/* Bottom row: Copyright */}
                  <div className="footer-bottom">
                    <span className="footer-credit">© 2026 BounceBunny</span>
                  </div>
                </div>
              </footer>

              <Toaster />
              <FeedbackIcon />
            </div>
          </TooltipProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
